import { DateTime, Interval } from 'luxon'
import { unselectableDaysOfWeek } from '@/legacy/types/global/dates'

const MILLISECONDS_PER_SECOND = 1000

export function stringToDateTime(s: string): DateTime
export function stringToDateTime(s: undefined): undefined
export function stringToDateTime(s: string | undefined): DateTime | undefined

/**
 * Converts a string to a DateTime object in UTC.
 * @param s
 */
export function stringToDateTime(s: string | undefined): DateTime | undefined {
  if (s) {
    const d = DateTime.fromISO(s, { zone: 'utc' })
    if (!d.isValid) {
      throw new Error(`Invalid date string: ${s}`)
    }
    return d
  }
}

export function stringToDateTimeLocal(s: string): DateTime
export function stringToDateTimeLocal(s: undefined | null): undefined
export function stringToDateTimeLocal(
  s: string | undefined
): DateTime | undefined

/**
 * Converts a string to a local DateTime object.
 * @param s
 */
export function stringToDateTimeLocal(
  s: string | null | undefined
): DateTime | undefined {
  if (s) {
    const d = DateTime.fromISO(s).toLocal()
    if (!d.isValid) {
      throw new Error(`Invalid date string: ${s}`)
    }
    return d
  }
}

export function dateTimeToString(d: DateTime): string
export function dateTimeToString(d: string): string
export function dateTimeToString(d: undefined): undefined
export function dateTimeToString(
  d: DateTime | string | undefined
): string | undefined
/**
 * Converts a DateTime to an ISO string. If a string is passed,
 * validates it as an ISO string and returns it.
 * @param d
 */
export function dateTimeToString(d: any): any {
  if (d) {
    // If already a string, validate it's a valid ISO string and return it
    if (typeof d === 'string') {
      const dt = DateTime.fromISO(d)
      return dt.toISO()
    }
    return d.toISO()
  }
}

/**
 *
 * @param d
 */
export function jsDateTimeToString(d: Date): string {
  return dateTimeToString(DateTime.fromJSDate(d))
}

/**
 * Converts a Date object to a UTC ISO string for API requests.
 * @param dateObj - The Date object to convert
 * @returns UTC ISO string or undefined if input is null/undefined
 */
export function getDateStringForAPI(
  dateObj: Date | null | undefined
): string | undefined {
  if (!dateObj) return undefined
  return DateTime.fromJSDate(dateObj).toUTC().toISO() || undefined
}

/**
 * Converts a JavaScript Date object to a plain date string in SQL format (YYYY-MM-DD).
 * Useful for removing time portion when only date is desired (JS Date always has time).
 * @param d
 */
export function jsDateToPlainDateString(d: Date): string {
  return DateTime.fromJSDate(d).toSQLDate() as string
}

/**
 *
 * @param s
 */
export function stringTojsDateTime(s: string): Date {
  return stringToDateTime(s).toJSDate()
}

/*
 * Pull off the year, month, and day from a luxon DateTime
 * to create a standard JS date with the same date. Since
 * JS dates use a timezone offset (e.g. -0700),
 * there can otherwise be an off-by-one error when
 * only looking at date and considering a UTC date object
 * when in another tz.
 *
 * e.g. 'Thu Apr 08 1992 17:00:00 GMT-0700' is the JS
 * representation of 4/9/92 (UTC) in Pacific Time. In a datepicker,
 * this should be 4/9/92, not 4/8/92
 */
/**
 *
 * @param d DateTime or null
 */
export function utcDatetimeToDate(d: DateTime | null): Date | null {
  return d ? new Date(d.year, d.month - 1, d.day) : null
}

/**
 * Converts a UTC datetime string from the database to a timezone-safe JS Date
 * suitable for use in date pickers. Strips time and timezone offset so the
 * date displays as entered regardless of the user's local timezone.
 * @param s UTC datetime string from the database, or null/undefined
 */
export function utcDateStringToDate(s: string | null | undefined): Date | null {
  return utcDatetimeToDate(stringToDateTime(s ?? undefined) ?? null)
}

/**
 * convert ISO string with timezone into
 * ISO string in UTC
 * @param iso
 * @returns string UTC ISO
 */
export function ISOToUTC(iso: string | null | undefined) {
  if (iso) {
    return DateTime.fromISO(maybeAddTimezone(iso), { setZone: true })
      .toUTC()
      .toISO()
  }
  console.error('expected ISO string but received null or undefined')
  return
}

// luxon Datetime
/**
 *
 * @param a
 * @param b
 */
export function compareNullableDatetimes(
  a: DateTime | undefined,
  b: DateTime | undefined
) {
  if (!b) {
    return -1
  }
  if (!a) {
    return 1
  }
  return a.toMillis() - b.toMillis()
}

/**
 *
 * @param d
 */
export function toSQLDate(d: Date | undefined) {
  return d ? `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}` : ''
}

export const getBetweenWeekendDayCount = (startDate: Date, endDate: Date) => {
  // This function counts and returns how many weekend
  // days are between given start and end date
  let count = 0
  const curDate = new Date(startDate)
  while (curDate <= endDate) {
    const dayOfWeek = curDate.getDay()
    if (unselectableDaysOfWeek.includes(dayOfWeek)) {
      count++
    }
    curDate.setDate(curDate.getDate() + 1)
  }
  return count
}

export const daysBetween = (startDate: DateTime, endDate: DateTime) => {
  const diff = Interval.fromDateTimes(endDate, startDate)
  return diff.length('days')
}

export const plusWeekdays = (startDate: DateTime, numDays: number) => {
  /*
  Adds a number of weekdays to a start date, always ending on a weekday
  (even if numDays = 0)
  */
  const NUM_WEEKDAYS = 5
  const LUXON_MONDAY = 1
  const LUXON_SATURDAY = 6
  const LUXON_SUNDAY = 7

  // weekday 1-7
  let dayOfWeek = startDate.weekday

  // If the current day is a weekend, offset it to the following Monday instead
  if (dayOfWeek === LUXON_SATURDAY || dayOfWeek === LUXON_SUNDAY) {
    if (dayOfWeek === LUXON_SATURDAY) {
      startDate = startDate.plus({ days: 2 })
    } else {
      startDate = startDate.plus({ days: 1 })
    }
    dayOfWeek = LUXON_MONDAY
    if (numDays > 0) {
      numDays -= 1
    }
  }

  let numWeeks = Math.floor(numDays / NUM_WEEKDAYS)
  let remainingDays = numDays % NUM_WEEKDAYS
  if (remainingDays + dayOfWeek > NUM_WEEKDAYS) {
    numWeeks += 1
    remainingDays = (remainingDays + dayOfWeek) % NUM_WEEKDAYS
  }

  const newDate = startDate.plus({ weeks: numWeeks, days: remainingDays })

  return newDate
}

export const maxDateWithWeekends = (
  minDate: Date,
  numDays: number,
  includeWeekends: boolean
) => {
  if (includeWeekends) {
    const lastDay = DateTime.fromJSDate(minDate).plus({ days: numDays })
    // Use this function to move the last day off of a weekend, if
    // it's on one
    return plusWeekdays(lastDay, 0).toJSDate()
  }
  return plusWeekdays(DateTime.fromJSDate(minDate), numDays).toJSDate()
}

/**
 * Backend does not include timezone and stores in UTC
 * This function adds timezone if it is not already present (UTC)
 * @param ISODate
 */
export function maybeAddTimezone(ISODate: string) {
  const splitDate = ISODate.split(/T\d+:\d+:\d+(?:\.\d+)*/g)
  if (splitDate.length > 1 && splitDate[1]) {
    return ISODate
  }
  return `${ISODate}Z`
}

/**
 *
 * @param ISODate
 */
export function fromISOWithZone(ISODate: string) {
  return DateTime.fromISO(maybeAddTimezone(ISODate), { setZone: true })
}

/**
 *
 * @param seconds
 * @returns number of seconds in milliseconds
 */
export function secondsToMilliseconds(seconds: number) {
  return seconds * MILLISECONDS_PER_SECOND
}

/**
 *
 * @param dateString
 */
export function fromISOStringWithoutTimeZone(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/**
 *
 * @param date datetime in UTC
 */
export function formatDateTimeYYYYMMDDHHMMSS(date: DateTime) {
  /**
   *
   * @param dateNumber
   */
  function padDate(dateNumber: number) {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return dateNumber < 10 ? `0${dateNumber}` : `${dateNumber}`
  }
  return `${date.year}${padDate(date.month)}${padDate(date.day)}T${padDate(
    date.hour
  )}${padDate(date.minute)}00`
}

/**
 * Format a date to locale date string
 * @param date
 */
export function formatDate(date: Date): string {
  return date.toLocaleDateString()
}

/**
 * Format a date to locale time string
 * @param date
 */
export function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}
